// Org admin organisation page — tabbed view with Users, Lookup Tables, and Settings tabs.

import { useState } from "react";
import { Users, Settings, TableProperties } from "lucide-react";
import { useCurrentOrganisation } from "../../hooks/useCurrentOrganisation";
import { LoadingSpinner, TabButton, Alert } from "../../components/common";
import OrgUsersTab from "./OrgUsersTab";
import OrgSettingsTab from "./OrgSettingsTab";
import LookupTablesTab from "../../components/admin/LookupTablesTab";

type Tab = "users" | "lookups" | "settings";

export default function OrganisationPage() {
  const [activeTab, setActiveTab] = useState<Tab>("users");
  const { data: org, isLoading, error } = useCurrentOrganisation();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !org) {
    return (
      <Alert variant="error">
        {error ? (error as Error).message : "Organisation not found"}
      </Alert>
    );
  }

  const tabs: { id: Tab; label: string; icon: typeof Users }[] = [
    { id: "users", label: "Users", icon: Users },
    { id: "lookups", label: "Lookup Tables", icon: TableProperties },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-h1 text-slate-900 dark:text-slate-50">
          {org.name}
        </h1>
        <p className="text-body-sm text-slate-500 mt-1">{org.id}</p>
      </div>

      <div className="border-b border-slate-200 dark:border-slate-700 mb-6">
        <div role="tablist" className="flex space-x-8">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              panelId={`${tab.id}-panel`}
              icon={<tab.icon className="h-4 w-4" />}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </TabButton>
          ))}
        </div>
      </div>

      {activeTab === "users" && (
        <div role="tabpanel" id="users-panel" aria-labelledby="users-panel-tab">
          <OrgUsersTab />
        </div>
      )}
      {activeTab === "lookups" && (
        <div
          role="tabpanel"
          id="lookups-panel"
          aria-labelledby="lookups-panel-tab"
        >
          <LookupTablesTab />
        </div>
      )}
      {activeTab === "settings" && (
        <div
          role="tabpanel"
          id="settings-panel"
          aria-labelledby="settings-panel-tab"
        >
          <OrgSettingsTab />
        </div>
      )}
    </div>
  );
}
